import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FetcherService} from '@core/services/fetcher.service';
import {NotificationsService} from '@core/services/notifications.service';
import {EMPTY, Observable, Subject, merge, throwError} from 'rxjs';
import {catchError, finalize, map, startWith, takeUntil} from 'rxjs/operators';

interface SelectOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-characteristic-milestone-link-create-dialog',
  templateUrl: './characteristic-milestone-link-create-dialog.component.html',
  styleUrls: ['../form-dialog/form-dialog.component.scss'],
})
export class CharacteristicMilestoneLinkCreateDialogComponent
  implements OnInit, OnDestroy
{
  readonly apiPrefix = '/backoffice/v2';
  form: FormGroup;
  confirmationVisible: boolean;
  isSaving = false;
  endCharacteristicExcludeIds: number[] = [];
  endMilestoneExcludeIds: number[] = [];
  startCharacteristicError$: Observable<string>;
  startMilestoneError$: Observable<string>;
  processError$: Observable<string>;
  orderError$: Observable<string>;
  readonly milestoneTooltipText =
    'Можно выбрать несколько событий. В отчёте будут использованы минимальная и максимальная даты из всех выбранных.';
  activeMilestoneTooltip: 'startMilestone' | 'endMilestone' | null = null;

  private readonly destroy$ = new Subject<boolean>();
  private readonly errorTrigger$ = new Subject<void>();
  private tooltipTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public dialogRef: MatDialogRef<CharacteristicMilestoneLinkCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      config?: {
        apiPrefix?: string;
      };
      rows?: any[];
      slug: string;
      systemName?: string;
      staticDataFilter?: any;
    },
    private readonly $fetcher: FetcherService,
    private readonly $notifications: NotificationsService,
  ) {}

  get isEditMode(): boolean {
    return !!this.data.rows?.length;
  }

  get submitLabel(): string {
    return this.isEditMode ? 'Сохранить' : 'Создать';
  }

  get title(): string {
    return this.isEditMode ? 'Изменить запись' : 'Создать запись';
  }

  get trackOn(): string {
    return this.isEditMode ? 'trackSaveEditEntryOnTable' : 'trackCreateOnTable';
  }

  private get startCharacteristicId(): number | null {
    return this.form?.get('startCharacteristicId')?.value?.id || null;
  }

  private get startMilestoneIds(): number[] {
    return this.getSelectedIds('startMilestoneIds');
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      startCharacteristicId: new FormControl(null, Validators.required),
      endCharacteristicId: new FormControl(null),
      startMilestoneIds: new FormControl([], Validators.required),
      endMilestoneIds: new FormControl([]),
      process: new FormControl(null, Validators.required),
      order: new FormControl(null, [
        Validators.required,
        Validators.pattern(/^\d+$/),
        Validators.min(1),
      ]),
    });
    const startCharacteristicControl = this.form.get('startCharacteristicId');
    const startMilestoneControl = this.form.get('startMilestoneIds');
    const processControl = this.form.get('process');
    const orderControl = this.form.get('order');
    this.startCharacteristicError$ = this.getControlErrorStream(
      startCharacteristicControl,
      this.getRequiredError,
    );
    this.startMilestoneError$ = this.getControlErrorStream(
      startMilestoneControl,
      this.getStartMilestoneError,
    );
    this.processError$ = this.getControlErrorStream(processControl, this.getProcessError);
    this.orderError$ = merge(
      orderControl.statusChanges,
      orderControl.valueChanges,
      this.errorTrigger$,
    ).pipe(startWith(null), map(() => this.getOrderError(orderControl)));

    const row = this.data.rows?.[0];
    if (row) {
      this.form.patchValue({
        startCharacteristicId: row.startCharacteristicId
          ? {id: row.startCharacteristicId, name: row.startCharacteristicName}
          : null,
        endCharacteristicId: row.endCharacteristicId
          ? {id: row.endCharacteristicId, name: row.endCharacteristicName}
          : null,
        startMilestoneIds: this.getMilestoneOptions(
          row.startMilestones,
          row.startMilestoneIds,
        ),
        endMilestoneIds: this.getMilestoneOptions(row.endMilestones, row.endMilestoneIds),
        process: row.process || null,
        order: row.order ?? null,
      });
    }

    this.form
      .get('startCharacteristicId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.endCharacteristicExcludeIds = value?.id ? [value.id] : [];
        const endControl = this.form.get('endCharacteristicId');
        if (value?.id && endControl.value?.id === value.id) {
          endControl.setValue(null);
        }
      });

    this.form
      .get('startMilestoneIds')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: SelectOption[] = []) => {
        const selectedIds = value.map(item => item.id);
        this.endMilestoneExcludeIds = selectedIds;
        const endControl = this.form.get('endMilestoneIds');
        const currentValue = (endControl.value as SelectOption[]) || [];
        const nextValue = currentValue.filter(item => !selectedIds.includes(item.id));

        if (nextValue.length !== currentValue.length) {
          endControl.setValue(nextValue);
        }
      });

    this.endCharacteristicExcludeIds = this.startCharacteristicId
      ? [this.startCharacteristicId]
      : [];
    this.endMilestoneExcludeIds = this.startMilestoneIds;

    if (this.isEditMode) {
      ['startCharacteristicId', 'endCharacteristicId'].forEach(controlName => {
        this.form.get(controlName).disable({emitEvent: false});
      });
    }
  }

  ngOnDestroy(): void {
    this.clearTooltipTimer();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  dismiss(): void {
    if (this.form.dirty) {
      this.confirmationVisible = true;
    } else {
      this.dialogRef.close();
    }
  }

  confirmedDismiss(confirmed: boolean): void {
    if (confirmed) {
      this.dialogRef.close();
    } else {
      this.confirmationVisible = false;
    }
  }

  save(): void {
    if (this.form.invalid || this.isSaving) {
      this.form.markAllAsTouched();
      this.errorTrigger$.next();
      return;
    }

    this.isSaving = true;

    const payload = {
      ...(this.data.staticDataFilter || {}),
      startCharacteristicId: this.form.get('startCharacteristicId').value.id,
      endCharacteristicId: this.form.get('endCharacteristicId').value?.id || null,
      startMilestoneIds: this.getSelectedIds('startMilestoneIds'),
      endMilestoneIds: this.getSelectedIds('endMilestoneIds'),
      process: this.form.get('process').value,
      order: Number(this.form.get('order').value),
    };

    const request = this.isEditMode
      ? this.$fetcher.putBySlug(
          this.data.slug,
          this.data.rows[0].id,
          payload,
          this.data.config?.apiPrefix || this.apiPrefix,
          this.data.systemName || 'bop',
        )
      : this.$fetcher.postBySlug(
          this.data.slug,
          [payload],
          this.data.config?.apiPrefix || this.apiPrefix,
          this.data.systemName || 'bop',
        );

    request
      .pipe(
        takeUntil(this.destroy$),
        catchError(response => {
          throwError(
            this.$notifications.showHttpError(
              response,
              this.isEditMode ? 'PUT' : 'POST',
              'Настройка связей для BI отчета',
            ),
          );
          return EMPTY;
        }),
        finalize(() => {
          this.isSaving = false;
        }),
      )
      .subscribe(response => {
        const data = Array.isArray(response?.data)
          ? response.data
          : [response?.data || {id: null}];
        this.dialogRef.close({data});
      });
  }

  private getControlErrorStream(
    control: AbstractControl,
    getError: (control: AbstractControl) => string,
  ): Observable<string> {
    return merge(control.statusChanges, control.valueChanges, this.errorTrigger$).pipe(
      startWith(null),
      map(() => getError.call(this, control)),
    );
  }

  private getRequiredError(control: AbstractControl): string {
    if (!control.touched || !control.hasError('required')) {
      return null;
    }

    return 'Это поле обязательное';
  }

  private getProcessError(control: AbstractControl): string {
    if (!control.touched) {
      return null;
    }

    if (control.hasError('required')) {
      return 'Это поле обязательное';
    }

    return null;
  }

  private getStartMilestoneError(control: AbstractControl): string {
    if (!control.touched || !control.hasError('required')) {
      return null;
    }

    return 'Выберите хотя бы одно событие начала';
  }

  private getOrderError(control: AbstractControl): string {
    if (!control.touched) {
      return null;
    }

    if (control.hasError('required')) {
      return 'Это поле обязательное';
    }

    if (control.hasError('pattern')) {
      return 'Значение не подходит по формату';
    }

    if (control.hasError('min')) {
      return 'Значение меньше минимального';
    }

    return null;
  }

  private getMilestoneOptions(
    milestones: SelectOption[] = [],
    milestoneIds: number[] = [],
  ): SelectOption[] {
    if (Array.isArray(milestones) && milestones.length > 0) {
      return milestones
        .filter(item => item?.id && item?.name)
        .map(item => ({id: item.id, name: item.name}));
    }

    if (Array.isArray(milestoneIds) && milestoneIds.length > 0) {
      return milestoneIds.map(id => ({id, name: String(id)}));
    }

    return [];
  }

  private getSelectedIds(controlName: string): number[] {
    return ((this.form?.get(controlName)?.value as SelectOption[]) || []).map(
      item => item.id,
    );
  }

  showMilestoneTooltip(tooltipType: 'startMilestone' | 'endMilestone'): void {
    this.clearTooltipTimer();
    this.activeMilestoneTooltip = tooltipType;
    this.tooltipTimer = setTimeout(() => {
      this.activeMilestoneTooltip = null;
      this.tooltipTimer = null;
    }, 4000);
  }

  private clearTooltipTimer(): void {
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
      this.tooltipTimer = null;
    }
    this.activeMilestoneTooltip = null;
  }
}
